import Vue from 'vue';
import { Module, VuexModule, Action, Mutation, getModule } from 'vuex-module-decorators';
import Session from '@/model/Session';
import api from '@/service/api';
import SessionListElement from '@/model/SessionListElement';
import Watermark from '@/model/Watermark';
import { SessionNotFound } from '@/error/sessionNotFound';
import { withSessionProtection } from '@/service/sessionProtection';
import Folder, { FolderState } from '@/model/Folder';

export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc',
}

export enum SortType {
    name = 'name',
    time = 'time',
}

export enum SessionErrorCode {
    SESSION_NOT_FOUND,
}

const { $vToastify: notifier } = new Vue() as any;
const watermarkUpdates = new Map<string, Promise<void>>();

function queueWatermarkUpdate(sessionId: string, update: () => Promise<void>): Promise<void> {
    const pending = (watermarkUpdates.get(sessionId) || Promise.resolve()).catch(() => {}).then(update);
    watermarkUpdates.set(sessionId, pending);
    return pending.finally(() => {
        if (watermarkUpdates.get(sessionId) === pending) watermarkUpdates.delete(sessionId);
    });
}

function partialPatchSession(patch: Pick<Session, 'id'> & Partial<Session>, session: Session) {
    const newSessionData = { ...session, ...patch };
    return new Session(
        newSessionData.id,
        newSessionData.name,
        newSessionData.watermark,
        newSessionData.pricelistId,
        newSessionData.finalizationEnabled,
        newSessionData.downloadEnabled,
        newSessionData.finalized,
        newSessionData.finalizationMode,
        newSessionData.orderCount,
        newSessionData.chatEnabled,
        newSessionData.welcomeMessageSetId,
    );
}

function syncSessionWithLists(updatedSession: Session, sessionList: { [userId: string]: SessionListElement[] }) {
    Object.entries(sessionList).forEach(([ userId, userSessions ]) => {
        sessionList[userId] = userSessions.map((listElement) => {
            if (listElement.id !== updatedSession.id) {
                return listElement;
            }

            return {
                ...listElement,
                name: updatedSession.name,
                watermark: updatedSession.watermark || { enabled: false },
                pricelistId: updatedSession.pricelistId,
                finalizationEnabled: updatedSession.finalizationEnabled,
                downloadEnabled: updatedSession.downloadEnabled,
                finalized: updatedSession.finalized,
                chatEnabled: updatedSession.chatEnabled,
                welcomeMessageSetId: updatedSession.welcomeMessageSetId,
            };
        });
    });
}

@Module({
    name: 'sessions',
})
export default class SessionsModule extends VuexModule {
    public loadInProgress: { [id: string]: Promise<void> } = {};
    public sessionListLoadInProgress: { [userId: string]: Promise<{ [userId: string]: SessionListElement[] }> | undefined } = {};
    public sessionErrors: { [id: string]: SessionErrorCode } = {};
    public sessions: { [id: string]: Session } = {};
    public sessionList: { [userId: string]: SessionListElement[] } = {};
    public sessionListLoading: boolean = false;
    public lastAddedSession: Session | null = null;
    public currentSession: Session | null = null;
    public creatingSession: boolean = false;
    public waitForWatermark: boolean = false;
    public watermarkProgress: { [sessionId: string]: { runId: number; completed: number; total: number } } = {};
    public watermarkRunIds: { [sessionId: string]: number } = {};
    public sortData: { [type in SortType]: SortDirection } & { current: SortType } = {
        [SortType.name]: SortDirection.ASC,
        [SortType.time]: SortDirection.ASC,
        current: SortType.time,
    };
    public folders: { [userId: string]: Folder[] } = {};
    public folderState: FolderState = {
        currentFolderId: null,
        isInsideFolder: false,
    };

    @Mutation
    public addLoadInProgress({ id, promise }: { id: string, promise: Promise<void> }) {
        this.loadInProgress[id] = promise;
    }

    @Mutation
    public deleteLoadInProgress(id: string) {
        delete this.loadInProgress[id];
    }

    @Mutation
    public addSessionListLoadInProgress({ userId, promise }: { userId: string, promise: Promise<{ [userId: string]: SessionListElement[] }> }) {
        this.sessionListLoadInProgress[userId] = promise;
    }

    @Mutation
    public deleteSessionListLoadInProgress(userId: string) {
        delete this.sessionListLoadInProgress[userId];
    }

    @Mutation
    public setCreatingSession(creating: boolean) {
        this.creatingSession = creating;
    }

    @Mutation
    public setCurrentSession(session: Session | null) {
        this.currentSession = session;
    }

    @Mutation
    public updateSort(type: SortType) {
        if (this.sortData.current !== type) {
            this.sortData.current = type;
        } else {
            this.sortData[type] = this.sortData[type] === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
        }
    }

    @Mutation
    public addSession(session: Session) {
        if (session.id) {
            Vue.set(
                this.sessions,
                session.id,
                session,
            );
            this.lastAddedSession = session;
        } else {
            throw new Error('Cannot add session without id to store');
        }
    }

    @Mutation
    public addSessionToList({ session, userId }: { session: Session, userId: string }) {
        if (session.id) {
            // Add to sessions object
            Vue.set(
                this.sessions,
                session.id,
                session,
            );
            this.lastAddedSession = session;

            // Also add to sessionList if it exists for this user
            if (this.sessionList[userId]) {
                const sessionListElement: SessionListElement = {
                    id: session.id,
                    name: session.data.name,
                    thumbnail: {
                        photoId: '',
                        url: '',
                    },
                    creationTime: new Date(),
                    watermark: { enabled: false },
                    shared: false,
                    pricelistId: null,
                    finalizationEnabled: false,
                    downloadEnabled: false,
                    packetId: null,
                    finalized: false,
                    ordered: false,
                    chatEnabled: false,
                    welcomeMessageSetId: null,
                };

                // Add to the beginning of the list (most recent first)
                this.sessionList[userId].unshift(sessionListElement);
            }
        } else {
            throw new Error('Cannot add session without id to store');
        }
    }

    @Mutation
    public patchSession(session: Session) {
        Vue.set(
            this.sessions,
            session.id,
            session,
        );

        syncSessionWithLists(session, this.sessionList);

        if (this.currentSession?.id === session.id) {
            this.currentSession = session;
        }
    }

    @Mutation
    public partialPatchSession(session: Pick<Session, 'id'> & Partial<Session>) {
        const originalSession = this.sessions[session.id];
        if (!originalSession) {
            throw new Error('Session not found for patching');
        }
        const patchedSession = partialPatchSession(session, originalSession);

        Vue.set(
            this.sessions,
            session.id,
            patchedSession,
        );

        syncSessionWithLists(patchedSession, this.sessionList);

        if (this.currentSession?.id === session.id) {
            this.currentSession = patchedSession;
        }
    }

    @Mutation
    public setSessionDeleted(sessionId: string) {
        Vue.delete(
            this.sessions,
            sessionId,
        );
        Object.entries(this.sessionList).forEach(([ userId, sessionList ]) => {
            this.sessionList[userId] = sessionList.filter((el) => el.id !== sessionId);
        });
    }

    @Mutation
    public setUserSessionList(sessionList: { [userId: string]: SessionListElement[] }) {
        this.sessionList = sessionList;
    }

    @Mutation
    public setWaitForWatermark(wait: boolean) {
        this.waitForWatermark = wait;
    }

    @Mutation
    public startWatermarkRendering({ sessionId, total, runId }: { sessionId: string; total: number; runId: number }) {
        if (runId < (this.watermarkRunIds[sessionId] || 0)) return;
        Vue.set(this.watermarkRunIds, sessionId, runId);
        if (!total) {
            Vue.delete(this.watermarkProgress, sessionId);
            return;
        }
        Vue.set(this.watermarkProgress, sessionId, { runId, completed: 0, total });
    }

    @Mutation
    public photoWatermarkRendered({ sessionId, runId }: { sessionId: string; runId: number }) {
        const progress = this.watermarkProgress[sessionId];
        if (progress && progress.runId === runId) Vue.set(this.watermarkProgress, sessionId, { ...progress, completed: progress.completed + 1 });
    }

    @Mutation
    public finishWatermarkRendering({ sessionId, runId }: { sessionId: string; runId: number }) {
        if (this.watermarkProgress[sessionId]?.runId !== runId) return;
        Vue.delete(this.watermarkProgress, sessionId);
    }

    @Mutation
    public setSessionShareStatus({ sessionId, shareStatus }: { sessionId: string, shareStatus: boolean }) {
        const userIds = Object.entries(this.sessionList)
            .filter(([ userId, sessionList ]) => sessionList.some((listElement) => listElement.id === sessionId))
            .map(([ userId ]) => userId);

        userIds.forEach((userId) => Vue.set(
            this.sessionList,
            userId,
            this.sessionList[userId].map(
                (listElement) => {
                    if (listElement.id === sessionId) {
                        listElement.shared = shareStatus;
                    }

                    return listElement;
                },
            ),
        ));
    }

    @Mutation
    public setSelectedPricelist({ sessionId, pricelistId }: { sessionId: string, pricelistId: string|null }) {
        const userIds = Object.entries(this.sessionList)
            .filter(([ _, sessionList ]) => sessionList.some((listElement) => listElement.id === sessionId))
            .map(([ userId ]) => userId);

        userIds.forEach((userId) => Vue.set(
            this.sessionList,
            userId,
            this.sessionList[userId].map(
                (listElement) => {
                    if (listElement.id === sessionId) {
                        listElement.pricelistId = pricelistId;
                    }

                    return listElement;
                },
            ),
        ));

        const patchedSession = partialPatchSession({ id: sessionId, pricelistId }, this.sessions[sessionId]);
        Vue.set(
            this.sessions,
            patchedSession.id,
            patchedSession,
        );
    }

    @Mutation
    public setSessionWelcomeMessageSet(
        { sessionId, welcomeMessageSetId }: { sessionId: string; welcomeMessageSetId: string | null },
    ) {
        const userIds = Object.entries(this.sessionList)
            .filter(([ _, sessionList ]) => sessionList.some((listElement) => listElement.id === sessionId))
            .map(([ userId ]) => userId);

        userIds.forEach((userId) => Vue.set(
            this.sessionList,
            userId,
            this.sessionList[userId].map(
                (listElement) => {
                    if (listElement.id === sessionId) {
                        listElement.welcomeMessageSetId = welcomeMessageSetId;
                    }

                    return listElement;
                },
            ),
        ));

        const session = this.sessions[sessionId];
        if (session) {
            session.welcomeMessageSetId = welcomeMessageSetId;
        }
    }

    @Mutation
    public clearSessionWelcomeMessageSetByIds(sessionIds: string[]) {
        if (sessionIds.length === 0) {
            return;
        }

        const idSet = new Set(sessionIds);

        Object.keys(this.sessions).forEach((sessionId) => {
            if (idSet.has(sessionId)) {
                this.sessions[sessionId].welcomeMessageSetId = null;
            }
        });

        Object.entries(this.sessionList).forEach(([ userId, sessionList ]) => {
            this.sessionList[userId] = sessionList.map((el) => {
                if (idSet.has(el.id)) {
                    return { ...el, welcomeMessageSetId: null };
                }

                return el;
            });
        });
    }

    @Mutation
    public setSelectedPacket({ sessionId, packetId }: { sessionId: string, packetId: string|null }) {
        const userIds = Object.entries(this.sessionList)
            .filter(([ _, sessionList ]) => sessionList.some((listElement) => listElement.id === sessionId))
            .map(([ userId ]) => userId);

        userIds.forEach((userId) => Vue.set(
            this.sessionList,
            userId,
            this.sessionList[userId].map(
                (listElement) => {
                    if (listElement.id === sessionId) {
                        listElement.packetId = packetId;
                    }

                    return listElement;
                },
            ),
        ));
    }

    @Mutation
    public setFinalizationEnabled({ sessionId, finalization }: { sessionId: string, finalization: boolean }) {
        const listElement = Object.values(this.sessionList).flat().find((el) => el.id === sessionId);
        if (listElement) {
            const userIds = Object.entries(this.sessionList)
                .filter(([ _, sessionList ]) => sessionList.some((listElement) => listElement.id === sessionId))
                .map(([ userId ]) => userId);

            userIds.forEach((userId) => Vue.set(
                this.sessionList,
                userId,
                this.sessionList[userId].map(
                    (listElement) => {
                        if (listElement.id === sessionId) {
                            listElement.finalizationEnabled = finalization;
                        }

                        return listElement;
                    },
                ),
            ));
        }
        const session = this.sessions[sessionId];
        if (session) {
            session.finalizationEnabled = finalization;
        }
    }

    @Mutation
    public setFinalizationMode({ sessionId, finalizationMode }: { sessionId: string, finalizationMode: boolean }) {
        if (!this.sessions[sessionId]) {
            throw new Error('Session not found');
        }
        const session = this.sessions[sessionId];
        if (session) {
            session.finalizationMode = finalizationMode;
        }

        Vue.set(
            this.sessions,
            sessionId,
            session,
        );
    }

    @Mutation
    public setSessionFinalized({ sessionId }: { sessionId: string }) {
        if (!this.sessions[sessionId]) {
            throw new Error('Session not found');
        }
        const session = this.sessions[sessionId];
        if (session) {
            session.finalized = true;
        }

        Vue.set(
            this.sessions,
            sessionId,
            session,
        );
        Object.entries(this.sessionList).forEach(([ userId, sessionList ]) => {
            this.sessionList[userId] = sessionList.map((el) => {
                if (el.id === sessionId) {
                    return { ...el, finalized: true };
                } else {
                    return el;
                }
            });
        });
    }

    @Mutation
    public setDownloadEnabled({ sessionId, download }: { sessionId: string, download: boolean }) {
        const listElement = Object.values(this.sessionList).flat().find((el) => el.id === sessionId);
        if (listElement) {
            const userIds = Object.entries(this.sessionList)
                .filter(([ _, sessionList ]) => sessionList.some((listElement) => listElement.id === sessionId))
                .map(([ userId ]) => userId);

            userIds.forEach((userId) => Vue.set(
                this.sessionList,
                userId,
                this.sessionList[userId].map(
                    (listElement) => {
                        if (listElement.id === sessionId) {
                            listElement.downloadEnabled = download;
                        }

                        return listElement;
                    },
                ),
            ));
        }
        const session = this.sessions[sessionId];
        if (session) {
            session.downloadEnabled = download;
        }
    }

    @Mutation
    public setSessionError({ id, error }: { id: string, error: SessionErrorCode }) {
        Vue.set(
            this.sessionErrors,
            id,
            error,
        );
    }

    @Mutation
    public clearSessionError(id: string) {
        Vue.delete(
            this.sessionErrors,
            id,
        );
    }

    @Mutation
    public setSessionListLoading(loading: boolean) {
        this.sessionListLoading = loading;
    }

    @Action({
        rawError: true,
    })
    public async editSession(id: string ) {
        this.clearSessionError(id);
        if (!!this.loadInProgress[id]) {
            return await this.loadInProgress[id];
        }

        if (!this.sessions[id]) {
            this.context.dispatch('requestSessionChats', [ id ]);
            const loadInProgress = api.loadSession(id);
            this.context.commit('addLoadInProgress', { id, promise: loadInProgress });
            try {
                this.context.commit('addSession', await loadInProgress);
            } catch (e) {
                if (e instanceof SessionNotFound) {
                    this.setSessionError({ id, error: SessionErrorCode.SESSION_NOT_FOUND });
                } else {
                    throw e;
                }
            } finally {
                this.context.commit('deleteLoadInProgress', id);
            }
        }
    }

    @Action({
        rawError: true,
    })
    public async loadSession(id: string) {
        this.context.commit('addSession', await api.loadSession(id));
    }

    @Action({
        rawError: true,
    })
    public async loadSessionIfNotExists(id: string) {
        if (!this.sessions[id]) {
            console.log('session doesn\'t exist, loading');
            await this.loadSession(id);
        }
    }

    @Action({ rawError: true })
    public async saveSession({ name, userId }: { name: string, userId: string }) {
        const id = await api.createSession(name);
        notifier.success({
            title: 'Utworzono',
            body: 'Sesja została dodana',
        });

        const newSession = new Session(id, name);
        this.addSessionToList({ session: newSession, userId });

        // If we're currently inside a folder, automatically add this session to it
        if (this.folderState.isInsideFolder && this.folderState.currentFolderId) {
            const currentFolder = this.currentFolder;
            if (currentFolder) {
                const updatedFolder = {
                    ...currentFolder,
                    sessionIds: [...currentFolder.sessionIds, id],
                };
                await this.updateFolderAndSave({ userId: currentFolder.userId, folder: updatedFolder });
            }
        }

        return newSession;
    }

    @Action({
        commit: 'patchSession',
        rawError: true,
    })
    public async updateSession(session: Session) {
        await api.updateSession(session);
        notifier.success({
            title: 'Zaktualizowano',
            body: 'Zmiany zostały zapisane',
        });

        return session;
    }

    @Action({
        rawError: true,
    })
    public async newOrderAdded({ sessionId, clientName, photoIds, requiresFinalization, finalizationMode }: { sessionId: string; clientName: string; photoIds: string[]; requiresFinalization: boolean; finalizationMode: boolean }) {
        const session = this.sessions[sessionId];
        const sessionName = session?.name;

        this.context.dispatch('markPhotosOrdered', {
            sessionId,
            clientName,
            photoIds,
            sessionName,
        }, { root: true });

        if (!session) return;

        const patch: Pick<Session, 'id'> & Partial<Session> = {
            id: sessionId,
            orderCount: session.orderCount + 1,
            finalizationMode,
        };
        if (requiresFinalization) {
            patch.finalized = false;
        }

        this.partialPatchSession(patch);
    }

    @Action({ rawError: true })
    public async createUnnamedSession(userId: string) {
        const name = 'Sesja bez nazwy';
        const id = await api.createSession(name);
        const newSession = new Session(id, name);

        this.addSessionToList({ session: newSession, userId });

        // If we're currently inside a folder, automatically add this session to it
        if (this.folderState.isInsideFolder && this.folderState.currentFolderId) {
            const currentFolder = this.currentFolder;
            if (currentFolder) {
                const updatedFolder = {
                    ...currentFolder,
                    sessionIds: [...currentFolder.sessionIds, id],
                };
                await this.updateFolderAndSave({ userId: currentFolder.userId, folder: updatedFolder });
            }
        }

        return newSession;
    }

    @Action({ rawError: true })
    public async createNamedSession({ name, userId }: { name: string, userId: string }) {
        const id = await api.createSession(name);
        const newSession = new Session(id, name);

        this.addSessionToList({ session: newSession, userId });

        // If we're currently inside a folder, automatically add this session to it
        if (this.folderState.isInsideFolder && this.folderState.currentFolderId) {
            const currentFolder = this.currentFolder;
            if (currentFolder) {
                const updatedFolder = {
                    ...currentFolder,
                    sessionIds: [...currentFolder.sessionIds, id],
                };
                await this.updateFolderAndSave({ userId: currentFolder.userId, folder: updatedFolder });
            }
        }

        return newSession;
    }

    @Action({ rawError: true, commit: 'setUserSessionList' })
    public async loadUserSessionList(userId: string, forceRefresh: boolean = false) {
        // Only show loading if sessions aren't already loaded for this user
        const sessionsAlreadyLoaded = !!this.sessionList[userId] && this.sessionList[userId].length > 0;

        // If sessions are already loaded and not forcing refresh, return cached data without API call
        if (sessionsAlreadyLoaded && !forceRefresh) {
            return { ...this.sessionList, [userId]: this.sessionList[userId] };
        }

        if (this.sessionListLoadInProgress[userId] && !forceRefresh) {
            return await this.sessionListLoadInProgress[userId];
        }

        const loadInProgress = (async () => {
            this.setSessionListLoading(true);
            const response = await api.loadUserSessionList(userId);
            this.context.dispatch('requestSessionChats', response.sessions.map((s) => s.id));

            // Update folders for this user
            if (!this.folders[userId]) {
                Vue.set(this.folders, userId, []);
            }
            this.folders[userId] = response.folders;

            return { ...this.sessionList, [userId]: response.sessions };
        })();

        this.context.commit('addSessionListLoadInProgress', { userId, promise: loadInProgress });

        try {
            return await loadInProgress;
        } finally {
            this.context.commit('deleteSessionListLoadInProgress', userId);
            this.setSessionListLoading(false);
        }
    }

    @Action({ rawError: true })
    public async chooseWatermark({
        sessionId,
        watermark,
    }: { sessionId: string, watermark: Watermark }) {
        await queueWatermarkUpdate(sessionId, () => withSessionProtection(async () => {
            if (!this.sessions[sessionId]) {
                this.addSession(await api.loadSession(sessionId));
            }
            try {
                this.sessions[sessionId].watermark = watermark;
                Object.entries(this.sessionList).forEach(([ userId, sessionList ]) => {
                    this.sessionList[userId] = sessionList.map((el) => {
                        if (el.id === sessionId) {
                            return { ...el, watermark };
                        } else {
                            return el;
                        }
                    });
                });
                await api.uploadWatermark(sessionId, watermark);
                await api.updateSession(this.sessions[sessionId], true);
                this.patchSession(this.sessions[sessionId]);
            } catch (error) {
                this.patchSession(await api.loadSession(sessionId));
                throw error;
            }
        }));
    }

    @Action({ rawError: true })
    public async deleteWatermark({
        sessionId,
    }: { sessionId: string }) {
        await queueWatermarkUpdate(sessionId, () => withSessionProtection(async () => {
            if (!this.sessions[sessionId]) {
                this.addSession(await api.loadSession(sessionId));
            }
            try {
                this.sessions[sessionId].watermark = { enabled: false };
                await api.deleteWatermark(sessionId);
                Object.entries(this.sessionList).forEach(([ userId, sessionList ]) => {
                    this.sessionList[userId] = sessionList.map((el) => {
                        if (el.id === sessionId) {
                            return { ...el, watermark: { enabled: false } };
                        } else {
                            return el;
                        }
                    });
                });
            } catch (error) {
                this.patchSession(await api.loadSession(sessionId));
                throw error;
            }
        }));
    }

    @Action({ rawError: true })
    public markSessionShared(sessionId: string) {
        this.setSessionShareStatus({ sessionId, shareStatus: true });
    }

    @Action({ rawError: true })
    public async selectPricelist({ sessionId, pricelistId }: { sessionId: string, pricelistId: string|null }) {
        await this.context.dispatch('selectPriceList', { sessionId, priceListId: pricelistId });
        this.setSelectedPricelist({
            sessionId,
            pricelistId,
        });
        try {
            await this.context.dispatch('refreshSessionSaleOptions', sessionId);
        } catch (_) {
            notifier.warning({ title: 'Cennik przypisany', body: 'Nie udało się odświeżyć ustawień zdjęć. Otwórz sesję ponownie.' });
        }
    }

    @Action({ rawError: true })
    public updateSessionWelcomeMessageSet(
        { sessionId, welcomeMessageSetId }: { sessionId: string; welcomeMessageSetId: string | null },
    ) {
        this.setSessionWelcomeMessageSet({ sessionId, welcomeMessageSetId });
    }

    @Action({ rawError: true })
    public clearWelcomeMessageSetForSessions(sessionIds: string[]) {
        this.clearSessionWelcomeMessageSetByIds(sessionIds);
    }

    @Action({ rawError: true })
    public async selectSessionPacket({ sessionId, packetId }: { sessionId: string, packetId: string|null }) {
        await this.context.dispatch('selectPacket', { sessionId, packetId });
        this.setSelectedPacket({
            sessionId,
            packetId,
        });
    }

    @Action({ rawError: true })
    public markSessionNotShared(sessionId: string) {
        this.setSessionShareStatus({ sessionId, shareStatus: false });
    }

    @Action({ rawError: true })
    public async deleteSession(sessionId: string) {
        const session = this.sessions[sessionId];
        if (session && session.orderCount > 0) {
            notifier.error({
                title: 'Nie można usunąć.',
                body: 'Sesja ma kupione zdjęcia.',
            });
            return false;
        }

        await api.deleteSession(sessionId);
        this.setSessionDeleted(sessionId);
        setTimeout(() => this.context.dispatch('refreshUsedStorageBytes'), 10000);
        return true;
    }

    @Action({ rawError: true })
    public async enableFinalization(sessionId: string) {
        this.setFinalizationEnabled({ sessionId, finalization: true });
        await api.enableSessionFinalization(sessionId);
    }

    @Action({ rawError: true })
    public async disableFinalization(sessionId: string) {
        this.setFinalizationEnabled({ sessionId, finalization: false });
        await api.disableSessionFinalization(sessionId);
    }

    @Action({ rawError: true })
    public async finalizeSession(sessionId: string) {
        await api.finalizeSession(sessionId);
        this.setSessionFinalized({ sessionId });
        this.setFinalizationMode({ sessionId, finalizationMode: false });
    }

    @Action({ rawError: true })
    public async enableDownload(sessionId: string) {
        this.setDownloadEnabled({ sessionId, download: true });
        await api.enableSessionDownload(sessionId);
    }

    @Action({ rawError: true })
    public async disableDownload(sessionId: string) {
        this.setDownloadEnabled({ sessionId, download: false });
        await api.disableSessionDownload(sessionId);
    }

    @Action({ rawError: true })
    public async enableFinalizationMode(sessionId: string) {
        this.setFinalizationMode({ sessionId, finalizationMode: true });
        await api.enableFinalizationMode(sessionId);
    }

    @Action({ rawError: true })
    public async disableFinalizationMode(sessionId: string) {
        this.setFinalizationMode({ sessionId, finalizationMode: false });
        await api.disableFinalizationMode(sessionId);
    }

    // Folder mutations
    @Mutation
    public addFolder({ userId, folder }: { userId: string, folder: Folder }) {
        if (!this.folders[userId]) {
            Vue.set(this.folders, userId, []);
        }
        this.folders[userId].push(folder);
    }

    @Mutation
    public removeFolder({ userId, folderId }: { userId: string, folderId: string }) {
        if (this.folders[userId]) {
            this.folders[userId] = this.folders[userId].filter((f) => f.id !== folderId);
        }
    }

    @Mutation
    public enterFolder(folderId: string) {
        this.folderState.currentFolderId = folderId;
        this.folderState.isInsideFolder = true;
    }

    @Mutation
    public exitFolder() {
        this.folderState.currentFolderId = null;
        this.folderState.isInsideFolder = false;
    }

    @Mutation
    public updateFolder({ userId, folder }: { userId: string, folder: Folder }) {
        if (this.folders[userId]) {
            const index = this.folders[userId].findIndex((f) => f.id === folder.id);
            if (index !== -1) {
                Vue.set(this.folders[userId], index, folder);
            }
        }
    }

    // Folder actions
    @Action
    public async createFolder({ userId, folderName, sessionIds }: { userId: string, folderName: string, sessionIds: string[] }) {
        const folder: Folder = {
            id: `folder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: folderName,
            sessionIds,
            creationTime: new Date(),
            userId,
        };
        this.addFolder({ userId, folder });

        // Save folders to backend
        await api.saveFolders(userId, this.folders[userId] || []);

        return folder;
    }

    @Action
    public async updateFolderAndSave({ userId, folder }: { userId: string, folder: Folder }) {
        this.updateFolder({ userId, folder });

        // Save folders to backend
        await api.saveFolders(userId, this.folders[userId] || []);
    }

    @Action
    public async deleteFolder({ userId, folderId }: { userId: string, folderId: string }) {
        this.removeFolder({ userId, folderId });
        if (this.folderState.currentFolderId === folderId) {
            this.exitFolder();
        }

        // Save folders to backend
        await api.saveFolders(userId, this.folders[userId] || []);
    }

    get sessionNames() {
        const names: { [id: string]: string } = {};
        Object.entries(this.sessions)
            .forEach(([ id, { name } ]) => names[id] = name);
        Object.values(this.sessionList).flat()
            .forEach((le) => names[le.id] = le.name);

        return names;
    }

    get sortedSessions() {
        const sortCompare = (a: SessionListElement, b: SessionListElement) => {
            const ascDesc = (compare: () => number) => (d: SortDirection) => compare() * (d === SortDirection.ASC ? 1 : -1);
            const compareFns = {
                [SortType.name]: ascDesc(() => a.name > b.name ? 1 : -1),
                [SortType.time]: ascDesc(() => a.creationTime > b.creationTime ? 1 : -1),
            };

            return compareFns[this.sortData.current](this.sortData[this.sortData.current]);
        };

        return Object.entries(this.sessionList)
            .map(([userId, list]) => ([
                userId,
                [...list].sort(sortCompare),
            ] as [string, SessionListElement[]]))
            .reduce((prev, [ userId, list ]) => ({ ...prev, [userId]: list}), {} as { [userId: string]: SessionListElement[] });
    }

    get filteredSessions() {
        if (!this.folderState.isInsideFolder || !this.folderState.currentFolderId) {
            return this.sortedSessions;
        }

        const currentFolder = Object.values(this.folders)
            .flat()
            .find((folder) => folder.id === this.folderState.currentFolderId);

        if (!currentFolder) {
            return this.sortedSessions;
        }

        return Object.entries(this.sortedSessions)
            .map(([userId, list]) => ([
                userId,
                list.filter((session) => currentFolder.sessionIds.includes(session.id)),
            ] as [string, SessionListElement[]]))
            .reduce((prev, [userId, list]) => ({ ...prev, [userId]: list }), {} as { [userId: string]: SessionListElement[] });
    }

    get sessionsNotInFolders() {
        return Object.entries(this.sortedSessions)
            .map(([userId, list]) => {
                const userFolders = this.folders[userId] || [];
                const sessionIdsInFolders = userFolders.flatMap((folder) => folder.sessionIds);

                return [
                    userId,
                    list.filter((session) => !sessionIdsInFolders.includes(session.id)),
                ] as [string, SessionListElement[]];
            })
            .reduce((prev, [userId, list]) => ({ ...prev, [userId]: list }), {} as { [userId: string]: SessionListElement[] });
    }

    get currentFolder() {
        if (!this.folderState.currentFolderId) {
            return null;
        }
        return Object.values(this.folders)
            .flat()
            .find((folder) => folder.id === this.folderState.currentFolderId) || null;
    }
}
